import { matchPath } from "react-router-dom";
import {
  HELP_ARTICLE_QUERY_PARAM,
  HELP_ARTICLE_SLUG_PATTERN,
  HELP_SCREEN_KEY_SEPARATOR,
} from "@/constants/help";
import {
  HELP_SCREEN_FALLBACK,
  HELP_SCREEN_KEYS,
  HELP_SCREEN_ROUTES,
} from "@/constants/helpScreens";
import type { HelpScreenContext, HelpScreenKey } from "@/interfaces/helpScreens";

/**
 * Resolves a pathname to its help-screen keys and (optional) rail label from HELP_SCREEN_ROUTES,
 * first matching row wins; anything unmatched gets HELP_SCREEN_FALLBACK.
 */
export function resolveHelpScreenContext(pathname: string): HelpScreenContext {
  for (const row of HELP_SCREEN_ROUTES) {
    for (const path of row.paths) {
      if (matchPath({ path, end: true }, pathname)) {
        return { keys: row.keys, label: row.label };
      }
    }
  }
  return HELP_SCREEN_FALLBACK;
}

/**
 * The keys the panel requests for a screen: every key mapped to it, which the API merges into one
 * cut (GFG-768). An empty list, which HELP_SCREEN_ROUTES never produces, falls back to dashboard.
 * This is the single place the requested key list is decided.
 */
export function helpScreenRequestKeys(keys: readonly HelpScreenKey[]): readonly HelpScreenKey[] {
  return keys.length > 0 ? keys : [HELP_SCREEN_KEYS.DASHBOARD];
}

/**
 * The GET /help/screen path for one or several keys: each key encoded as its own segment value,
 * joined by a literal HELP_SCREEN_KEY_SEPARATOR, e.g. `/help/screen/settings,wallet`.
 */
export function buildHelpScreenPath(contextKeys: readonly HelpScreenKey[]): string {
  if (contextKeys.length === 0) {
    console.error("[help] refused an empty help-screen key list");
    throw new Error("That help link isn't valid.");
  }
  return `/help/screen/${contextKeys.map(encodeHelpPathSegment).join(HELP_SCREEN_KEY_SEPARATOR)}`;
}

/**
 * Builds the `/support` deep link for an article slug — used as the internal-link renderer's
 * `href` so a cmd/ctrl-click, middle-click or copied link opens a route the app actually serves.
 */
export function buildHelpArticleHref(slug: string): string {
  return `/support?${HELP_ARTICLE_QUERY_PARAM}=${encodeURIComponent(slug)}`;
}

/** The value itself when it is shaped like an article slug, otherwise null. */
export function parseHelpArticleSlug(value: string | null): string | null {
  return value !== null && HELP_ARTICLE_SLUG_PATTERN.test(value) ? value : null;
}

/**
 * Encodes caller input as exactly one path segment of a help API URL. `encodeURIComponent` leaves
 * "." and ".." intact, and URL parsing resolves those (and their `%2e` forms) as dot-segments that
 * climb out of the route, so they are refused rather than sent.
 */
export function encodeHelpPathSegment(value: string): string {
  if (value === "" || value === "." || value === "..") {
    console.error("[help] refused a help API path segment", { value });
    throw new Error("That help link isn't valid.");
  }
  return encodeURIComponent(value);
}
